import { useCallback, useRef } from 'react';

/**
 * 로딩 상태를 내부적으로 추적하여 중복 API 호출을 방지하는 커스텀 훅
 * 실행 함수만 반환하며 로딩 상태는 UI에 노출되지 않음
 * 
 * @param asyncFunction - 실행할 비동기 함수 (Promise를 반환해야 함)
 * @returns 중복 호출을 방지하는 실행 함수
 */
export const useAsyncAction = <T extends (...args: any[]) => Promise<any>>(
  asyncFunction: T
): ((...args: Parameters<T>) => Promise<ReturnType<T> | void>) => {
  const isLoadingRef = useRef(false);

  const execute = useCallback(
    async (...args: Parameters<T>): Promise<ReturnType<T> | void> => {
      // 중복 호출 방지
      if (isLoadingRef.current) {
        return;
      }

      isLoadingRef.current = true;

      try {
        const result = await asyncFunction(...args);
        return result;
      } catch (error) {
        throw error;
      } finally {
        isLoadingRef.current = false;
      }
    },
    [asyncFunction]
  );

  return execute;
};